//frontend/src/pages/Home.js
import React, { useState, useEffect } from 'react';
import { 
    Avatar, 
    Box, 
    Button, 
    Card, 
    CardContent, 
    Container, 
    Typography, 
    IconButton,
    CircularProgress,
    Paper
} from '@mui/material';
import { 
    Person as PersonIcon,
    Notifications as NotificationsIcon,
    ChevronRight as ChevronRightIcon,
    ListAlt as ListAltIcon,
    CalendarToday as CalendarTodayIcon,
    People as PeopleIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { isAuthenticated } from '../utils/auth';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../utils/axios';

function Home() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({
        headerImage: 'crm.png',
        logoImage: 'favicon.png',
        promoText: 'Enjoy a 15% discount on website development until the end of May',
        headerTitle: 'Aunimeda',
        headerSubtitle: 'Software Company'
    });

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axiosInstance.get('/settings');
            if (response.data) {
                setSettings(response.data);
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleProfileClick = () => {
        if (isAuthenticated()) {
            navigate('/dashboard');
        } else {
            navigate('/auth');
        }
    };

    const handleServiceClick = () => {
        navigate('/select-service');
    };

    const handleSpecialistClick = () => {
        navigate('/select-specialist');
    };

    const handleDateTimeClick = () => {
        navigate('/select-datetime');
    };

    if (loading) {
        return (
            <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Container>
        );
    }

    return (
        <Container maxWidth="sm" sx={{ mt: 4 }}>
            <Box sx={{ position: 'relative', mb: 3 }}>
                <img
                    src={settings.headerImage}
                    alt="Background"
                    style={{ 
                        width: '100%', 
                        borderRadius: '10px',
                        height: 'auto',
                        maxHeight: '200px',
                        objectFit: 'cover'
                    }}
                    onError={(e) => {
                        e.target.src = 'crm.png'; // Fallback image if the custom one fails to load
                    }}
                />
                <IconButton
                    onClick={handleProfileClick}
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        backgroundColor: '#ffffff',
                        boxShadow: 2,
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    <PersonIcon />
                </IconButton>
            </Box>

            <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Avatar
                    src={settings.logoImage}
                    sx={{
                        width: 80,
                        height: 80,
                        margin: 'auto',
                        border: '2px solid white',
                        position: 'relative',
                        top: -40,
                        zIndex: 1,
                        backgroundColor: '#ffffff',
                    }}
                    onError={(e) => {
                        e.target.src = 'favicon.png'; // Fallback logo if the custom one fails to load
                    }}
                />
                <Typography variant="h5" sx={{ fontWeight: 'bold', mt: -2 }}>
                    {settings.headerTitle}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    {settings.headerSubtitle}
                </Typography>
            </Box>

            <Card
                sx={{
                    mb: 3,
                    backgroundColor: '#f0f4ff',
                    borderRadius: 2,
                    boxShadow: 1,
                }}
            >
                <CardContent sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '16px',
                    '&:last-child': { paddingBottom: '16px' }
                }}>
                    <Typography variant="body2" sx={{ flex: 1, mr: 2 }}>
                        {settings.promoText}
                    </Typography>
                    <IconButton size="small">
                        <NotificationsIcon />
                    </IconButton>
                </CardContent>
            </Card>

            <Paper
   elevation={0}
   sx={{
       mt: 4,
       borderRadius: 2,
       bgcolor: 'background.paper',
       overflow: 'hidden'
   }}
>
   <Box 
       onClick={() => navigate('/select-service')}
       sx={{
           display: 'flex',
           alignItems: 'center',
           p: 2.5,
           cursor: 'pointer',
           borderBottom: '1px solid rgba(0,0,0,0.1)',
           transition: '0.2s',
           '&:hover': {
               bgcolor: 'rgba(0,0,0,0.02)'
           }
       }}
   >
       <Box sx={{ 
           width: 48,
           height: 48,
           borderRadius: '12px',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           bgcolor: 'primary.main',
           color: 'white',
           mr: 2.5
       }}>
           <ListAltIcon sx={{ fontSize: 24 }} />
       </Box>
       <Box sx={{ flex: 1 }}>
           <Typography variant="subtitle1" fontWeight={500}>
               {t('common.selectservices')}
           </Typography>
       </Box>
       <ChevronRightIcon color="action" />
   </Box>

   <Box 
       sx={{
           display: 'flex',
           alignItems: 'center',
           p: 2.5,
           borderBottom: '1px solid rgba(0,0,0,0.1)',
           opacity: 0.5
       }}
   >
       <Box sx={{ 
           width: 48,
           height: 48,
           borderRadius: '12px',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           bgcolor: 'grey.300',
           mr: 2.5
       }}>
           <PeopleIcon sx={{ fontSize: 24 }} />
       </Box>
       <Typography variant="subtitle1" fontWeight={500} sx={{ flex: 1 }}>
           {t('common.selectspecialist')}
       </Typography>
       <ChevronRightIcon color="disabled" />
   </Box>

   <Box 
       sx={{
           display: 'flex',
           alignItems: 'center',
           p: 2.5,
           opacity: 0.5
       }}
   >
       <Box sx={{ 
           width: 48,
           height: 48,
           borderRadius: '12px',
           display: 'flex',
           alignItems: 'center',
           justifyContent: 'center',
           bgcolor: 'grey.300',
           mr: 2.5
       }}>
           <CalendarTodayIcon sx={{ fontSize: 24 }} />
       </Box>
       <Typography variant="subtitle1" fontWeight={500} sx={{ flex: 1 }}>
           {t('common.selectdateandtime')}
       </Typography>
       <ChevronRightIcon color="disabled" />
   </Box>
</Paper>
        </Container>
    );
}

export default Home;